'use client'

import { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import { useRouter, useSearchParams, usePathname } from 'next/navigation'
import { TeamContext } from '@/contexts/TeamContext'
import { AuthContext } from '@/contexts/AuthContext'
import { fetcher } from '@/lib/api/fetch'
import {
  getStoredTeamContext,
  setStoredTeamContext,
  parseTeamContextFromUrl,
  formatTeamContextForUrl,
} from '@/lib/teamContext'
import { objectIdIsEmpty } from '@/lib/utils/common'

export default function TeamProvider({ children }) {
  const authContext = useContext(AuthContext)
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [activeTeamId, setActiveTeamId] = useState(null)
  const [teams, setTeams] = useState([])
  const [loading, setLoading] = useState(false)
  const [initialized, setInitialized] = useState(false)

  // Fetch teams for the current organization (excludes archived teams by default)
  const fetchTeams = useCallback(async () => {
    if (
      !authContext.user?.organization?.id ||
      objectIdIsEmpty(authContext.user.organization.id) ||
      !authContext.user?.loaded
    )
      return

    setLoading(true)
    try {
      // Explicitly request only non-archived teams for team context
      const res = await fetcher(
        `/api/admin/organization/${authContext.user.organization.id}/teams?archived=false`,
        { method: 'GET' },
        authContext,
      )

      if (res.status === 200) {
        const data = await res.json()
        // Filter out any archived teams (additional safety check)
        const activeTeams = (data.teams || []).filter(team => !team.archived)
        setTeams(activeTeams)
      } else if (res.status === 403) {
        // User doesn't have permission to view teams, set empty array silently
        setTeams([])
      } else {
        console.error('Failed to fetch teams', res.status)
        setTeams([])
      }
    } catch (error) {
      console.error('Error fetching teams:', error)
      setTeams([])
    } finally {
      setLoading(false)
    }
  }, [
    authContext.user?.organization?.id,
    authContext.user?.loaded,
    authContext,
  ])

  // Initialize team context from URL or localStorage
  useEffect(() => {
    if (
      !authContext.user?.organization?.id ||
      objectIdIsEmpty(authContext.user.organization.id) ||
      initialized
    )
      return

    // Check URL parameter first
    const contextParam = searchParams.get('context')
    let initialTeamId = null

    if (contextParam) {
      initialTeamId = parseTeamContextFromUrl(contextParam)
    } else {
      // Fall back to localStorage
      initialTeamId = getStoredTeamContext()
    }

    setActiveTeamId(initialTeamId)
    setInitialized(true)
  }, [searchParams, authContext.user?.organization?.id, initialized])

  // Fetch teams when organization changes and user is loaded
  useEffect(() => {
    if (
      authContext.user?.organization?.id &&
      !objectIdIsEmpty(authContext.user.organization.id) &&
      authContext.user?.loaded
    ) {
      fetchTeams()
    }
  }, [authContext.user?.organization?.id, authContext.user?.loaded, fetchTeams])

  // Validate that activeTeamId exists in teams list
  useEffect(() => {
    if (activeTeamId && teams.length > 0 && initialized) {
      const teamExists = teams.some(t => t.id === activeTeamId)
      if (!teamExists) {
        // Team was removed or user lost access, reset to org context
        setActiveTeamId(null)
        setStoredTeamContext(null)
        updateUrl(null)
      }
    }
  }, [activeTeamId, teams, initialized])

  const updateUrl = useCallback(
    teamId => {
      const params = new URLSearchParams(searchParams.toString())
      const contextValue = formatTeamContextForUrl(teamId)

      if (contextValue === 'org') {
        params.delete('context')
      } else {
        params.set('context', contextValue)
      }

      const newUrl = params.toString()
        ? `${pathname}?${params.toString()}`
        : pathname

      router.replace(newUrl, { scroll: false })
    },
    [pathname, router, searchParams],
  )

  const setActiveTeam = useCallback(
    teamId => {
      setActiveTeamId(teamId)
      setStoredTeamContext(teamId)
      updateUrl(teamId)
    },
    [updateUrl],
  )

  const value = useMemo(
    () => ({
      activeTeamId,
      teams,
      loading,
      setActiveTeam,
      refreshTeams: fetchTeams,
    }),
    [activeTeamId, teams, loading, setActiveTeam, fetchTeams],
  )

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>
}
